import React, { useCallback, useEffect, useState } from "react";
import {
  FiCheck,
  FiEdit2,
  FiImage,
  FiPackage,
  FiPlus,
  FiPower,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import CatalogMenuManagerModal from "../components/CatalogMenuManagerModal";

const EMPTY_CATALOG_FORM = {
  name: "",
  outlet_id: "",
  description: "",
  is_active: true,
};

const GUEST_THEME_OPTIONS = [
  { value: "light", label: "White", background: "#f3f5f7", surface: "#ffffff", accent: "#0d7f6c" },
  { value: "dark", label: "Black", background: "#0b0d10", surface: "#20242c", accent: "#38c8aa" },
  { value: "gray", label: "Gray", background: "#34383e", surface: "#4d535b", accent: "#f0c75e" },
  { value: "navy", label: "Navy", background: "#0b1b2b", surface: "#1d3850", accent: "#6ec6e8" },
  { value: "forest", label: "Forest", background: "#13231a", surface: "#2b4635", accent: "#b8d77a" },
  { value: "burgundy", label: "Burgundy", background: "#28151a", surface: "#4d2a33", accent: "#e6b976" },
];

const GUEST_LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "nl", label: "Dutch" },
  { value: "ca", label: "Catalan" },
];

function getBusinessUnitId(searchParams) {
  const user = readStoredUser();
  return searchParams.get("business_unit_id") || user?.active_business_unit_id || user?.business_unit_ids?.[0] || "1";
}

export default function Catalogs() {
  const [searchParams] = useSearchParams();
  const businessUnitId = getBusinessUnitId(searchParams);
  const [catalogs, setCatalogs] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [outletFilter, setOutletFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [catalogModalMode, setCatalogModalMode] = useState(null);
  const [catalogForm, setCatalogForm] = useState(EMPTY_CATALOG_FORM);
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [savingCatalog, setSavingCatalog] = useState(false);

  const [appearanceCatalog, setAppearanceCatalog] = useState(null);
  const [appearanceTheme, setAppearanceTheme] = useState("dark");
  const [appearanceEnabledLanguages, setAppearanceEnabledLanguages] = useState(["en"]);
  const [appearanceDefaultLanguage, setAppearanceDefaultLanguage] = useState("en");
  const [appearanceLogoFile, setAppearanceLogoFile] = useState(null);
  const [appearanceLogoPreview, setAppearanceLogoPreview] = useState("");
  const [appearanceBackgroundFile, setAppearanceBackgroundFile] = useState(null);
  const [appearanceBackgroundPreview, setAppearanceBackgroundPreview] = useState("");
  const [savingAppearance, setSavingAppearance] = useState(false);

  const [manageCatalog, setManageCatalog] = useState(null);

  const loadCatalogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/pos/catalogs", {
        params: {
          business_unit_id: businessUnitId,
          q: debouncedSearchQuery,
          outlet_id: outletFilter,
          is_active: statusFilter,
        },
      });
      setCatalogs(res.data?.rows || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load POS catalogs.");
    } finally {
      setLoading(false);
    }
  }, [businessUnitId, debouncedSearchQuery, outletFilter, statusFilter]);

  const loadSupportData = useCallback(async () => {
    const outletRes = await axios.get("/api/pos/outlets", { params: { business_unit_id: businessUnitId } });
    setOutlets(outletRes.data?.options || []);
  }, [businessUnitId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    loadSupportData().catch((err) => setError(err?.response?.data?.error || "Could not load POS setup data."));
  }, [loadSupportData]);


  const clearCatalogFilters = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setOutletFilter("");
    setStatusFilter("");
  };

  const openCreateCatalog = () => {
    setError("");
    setMessage("");
    setCatalogModalMode("create");
    setEditingCatalogId(null);
    setCatalogForm(EMPTY_CATALOG_FORM);
  };

  const openEditCatalog = (catalog) => {
    setError("");
    setMessage("");
    setCatalogModalMode("edit");
    setEditingCatalogId(catalog.id);
    setCatalogForm({
      name: catalog.name || "",
      outlet_id: catalog.outlet_id || "",
      description: catalog.description || "",
      is_active: catalog.is_active !== false,
    });
  };

  const closeCatalogModal = () => {
    if (savingCatalog) return;
    setCatalogModalMode(null);
    setEditingCatalogId(null);
    setCatalogForm(EMPTY_CATALOG_FORM);
  };

  const saveCatalog = async () => {
    const name = catalogForm.name.trim();
    if (!name) {
      setError("Catalog name is required.");
      return;
    }
    try {
      setSavingCatalog(true);
      setError("");
      setMessage("");
      const payload = { ...catalogForm, name, business_unit_id: businessUnitId };
      if (catalogModalMode === "edit" && editingCatalogId) {
        await axios.patch(`/api/pos/catalogs/${editingCatalogId}`, payload);
        setMessage(`${name} updated.`);
      } else {
        await axios.post("/api/pos/catalogs", payload);
        setMessage(`${name} created. Build its guest menu before assigning it to devices.`);
      }
      setCatalogModalMode(null);
      setEditingCatalogId(null);
      setCatalogForm(EMPTY_CATALOG_FORM);
      await Promise.all([loadCatalogs(), loadSupportData()]);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save POS catalog.");
    } finally {
      setSavingCatalog(false);
    }
  };

  const toggleCatalogStatus = async (catalog) => {
    try {
      setError("");
      setMessage("");
      await axios.patch(`/api/pos/catalogs/${catalog.id}`, {
        business_unit_id: businessUnitId,
        is_active: !catalog.is_active,
      });
      setMessage(`${catalog.name} ${catalog.is_active ? "deactivated" : "activated"}.`);
      await loadCatalogs();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update POS catalog.");
    }
  };

  const openAppearance = (catalog) => {
    setError("");
    setMessage("");
    setAppearanceCatalog(catalog);
    setAppearanceTheme(catalog.guest_theme || "dark");
    const enabledLanguages = catalog.guest_enabled_languages?.length
      ? catalog.guest_enabled_languages
      : [catalog.guest_default_language || "en"];
    setAppearanceEnabledLanguages(enabledLanguages);
    setAppearanceDefaultLanguage(
      enabledLanguages.includes(catalog.guest_default_language)
        ? catalog.guest_default_language
        : enabledLanguages[0]
    );
    setAppearanceLogoFile(null);
    setAppearanceLogoPreview(catalog.guest_logo_url || "");
    setAppearanceBackgroundFile(null);
    setAppearanceBackgroundPreview(catalog.guest_background_url || "");
  };

  const closeAppearance = () => {
    if (savingAppearance) return;
    setAppearanceCatalog(null);
    setAppearanceLogoFile(null);
    setAppearanceLogoPreview("");
    setAppearanceBackgroundFile(null);
    setAppearanceBackgroundPreview("");
  };

  const selectAppearanceLogo = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!(["image/jpeg", "image/png", "image/webp"].includes(file.type))) {
      setError("Use a JPG, PNG or WEBP logo.");
      event.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Logo files can be up to 5 MB.");
      event.target.value = "";
      return;
    }
    setError("");
    setAppearanceLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setAppearanceLogoPreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const selectAppearanceBackground = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;
    if (!(["image/jpeg", "image/png", "image/webp"].includes(file.type))) {
      setError("Use a JPG, PNG or WEBP background image.");
      event.target.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Background images can be up to 10 MB.");
      event.target.value = "";
      return;
    }
    setError("");
    setAppearanceBackgroundFile(file);
    const reader = new FileReader();
    reader.onload = () => setAppearanceBackgroundPreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const saveAppearance = async () => {
    if (!appearanceCatalog?.id) return;
    try {
      setSavingAppearance(true);
      setError("");
      setMessage("");
      await axios.patch(`/api/pos/catalogs/${appearanceCatalog.id}`, {
        business_unit_id: businessUnitId,
        guest_theme: appearanceTheme,
        guest_enabled_languages: appearanceEnabledLanguages,
        guest_default_language: appearanceDefaultLanguage,
      });
      if (appearanceLogoFile) {
        const body = new FormData();
        body.append("business_unit_id", businessUnitId);
        body.append("file", appearanceLogoFile);
        await axios.post(`/api/pos/catalogs/${appearanceCatalog.id}/guest-logo`, body);
      }
      if (appearanceBackgroundFile) {
        const body = new FormData();
        body.append("business_unit_id", businessUnitId);
        body.append("file", appearanceBackgroundFile);
        await axios.post(`/api/pos/catalogs/${appearanceCatalog.id}/guest-background`, body);
      }
      setAppearanceCatalog(null);
      setAppearanceLogoFile(null);
      setAppearanceLogoPreview("");
      setAppearanceBackgroundFile(null);
      setAppearanceBackgroundPreview("");
      setMessage(`${appearanceCatalog.name} appearance updated.`);
      await loadCatalogs();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update catalog appearance.");
      await loadCatalogs();
    } finally {
      setSavingAppearance(false);
    }
  };

  const removeAppearanceLogo = async () => {
    if (!appearanceCatalog?.id) return;
    if (appearanceLogoFile) {
      setAppearanceLogoFile(null);
      setAppearanceLogoPreview(appearanceCatalog.guest_logo_url || "");
      return;
    }
    try {
      setSavingAppearance(true);
      setError("");
      const res = await axios.delete(`/api/pos/catalogs/${appearanceCatalog.id}/guest-logo`, {
        data: { business_unit_id: businessUnitId },
      });
      const updated = res.data?.catalog || { ...appearanceCatalog, guest_logo_url: null, guest_logo_asset_id: null };
      setAppearanceCatalog(updated);
      setAppearanceLogoPreview("");
      setMessage(`${appearanceCatalog.name} logo removed.`);
      await loadCatalogs();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not remove catalog logo.");
    } finally {
      setSavingAppearance(false);
    }
  };

  const removeAppearanceBackground = async () => {
    if (!appearanceCatalog?.id) return;
    if (appearanceBackgroundFile) {
      setAppearanceBackgroundFile(null);
      setAppearanceBackgroundPreview(appearanceCatalog.guest_background_url || "");
      return;
    }
    try {
      setSavingAppearance(true);
      setError("");
      const res = await axios.delete(`/api/pos/catalogs/${appearanceCatalog.id}/guest-background`, {
        data: { business_unit_id: businessUnitId },
      });
      const updated = res.data?.catalog || {
        ...appearanceCatalog,
        guest_background_url: null,
        guest_background_asset_id: null,
      };
      setAppearanceCatalog(updated);
      setAppearanceBackgroundPreview("");
      setMessage(`${appearanceCatalog.name} background removed.`);
      await loadCatalogs();
    } catch (err) {
      setError(err?.response?.data?.error || "Could not remove catalog background.");
    } finally {
      setSavingAppearance(false);
    }
  };

  const openProductManager = (catalog) => {
    setError("");
    setMessage("");
    setManageCatalog(catalog);
  };

  const closeProductManager = () => {
    setManageCatalog(null);
  };

  const toggleAppearanceLanguage = (language) => {
    setAppearanceEnabledLanguages((current) => {
      if (current.includes(language)) {
        if (current.length === 1) return current;
        const next = current.filter((value) => value !== language);
        if (appearanceDefaultLanguage === language) setAppearanceDefaultLanguage(next[0]);
        return next;
      }
      return [...current, language];
    });
  };

  return (
    <div className="reservations-container rm-page">
      <div className="rm-toolbarCard">
        <div className="rm-toolbarRow">
          <div>
            <h3 className="rm-rates-title">Guest catalogs</h3>
            <div className="rm-rates-subtitle">Create guest-facing menus and link every item to its operational POS product.</div>
          </div>
          <div className="rm-topActions">
            <input
              className="rm-searchInput"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search catalog or outlet"
            />
            <select className="posSelect posCatalogToolbarSelect" value={outletFilter} onChange={(event) => setOutletFilter(event.target.value)}>
              <option value="">All outlets</option>
              {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}
            </select>
            <select className="posSelect posDeviceStatusSelect" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button type="button" className="rm-topBtn" onClick={clearCatalogFilters}><FiX /> Clear</button>
            <button type="button" className="rm-topBtn rm-topBtn--green" onClick={openCreateCatalog}><FiPlus /> Add catalog</button>
          </div>
        </div>
      </div>

      {error ? <div className="posStateBanner">{error}</div> : null}
      {message ? <div className="posInfoBanner">{message}</div> : null}

      <div className="rm-rates-card">
        <div className="rm-rates-header">
          <h3 className="rm-rates-title">Catalog setup</h3>
          <div className="rm-rates-subtitle">Define the guest content, languages and POS links before assigning a catalog to devices.</div>
        </div>
        <div className="res-listMetaRow rm-listMetaRow">
          <small className="res-listMetaText">{loading ? "Loading catalogs..." : `${catalogs.length} catalogs`}</small>
        </div>
        <div className="rm-rates-table-wrap">
          <table className="rm-rates-table" style={{ "--rm-col1-width": "260px", "--rm-table-min-width": "1260px" }}>
            <colgroup>
              <col style={{ width: "260px" }} />
              <col style={{ width: "190px" }} />
              <col style={{ width: "330px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "440px" }} />
            </colgroup>
            <thead>
              <tr>
                <th className="res-th rm-sticky-1"><div className="res-thBtn"><span className="res-thLabel">Catalog</span><span className="res-thSub">Menu name</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Outlet</span><span className="res-thSub">Sales area</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Description</span><span className="res-thSub">Purpose</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Menu items</span><span className="res-thSub">Guest-facing</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Devices</span><span className="res-thSub">Assigned</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Status</span><span className="res-thSub">Visibility</span></div></th>
                <th className="res-th"><div className="res-thBtn"><span className="res-thLabel">Actions</span><span className="res-thSub">Configuration</span></div></th>
              </tr>
            </thead>
            <tbody>
              {catalogs.map((catalog) => (
                <tr key={catalog.id}>
                  <td className="rm-sticky-1"><strong className="posDeviceName">{catalog.name}</strong></td>
                  <td>{catalog.outlet_name || "-"}</td>
                  <td><span className="posCatalogDescription">{catalog.description || "-"}</span></td>
                  <td>{Number(catalog.product_count || 0).toLocaleString("es-ES")}</td>
                  <td>{Number(catalog.device_count || 0).toLocaleString("es-ES")}</td>
                  <td><span className={`rm-chip ${catalog.is_active ? "rm-chip--ok" : "rm-chip--off"}`}>{catalog.is_active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <div className="posDeviceActions">
                      <button type="button" className="rm-topBtn rm-topBtn--cyan" onClick={() => openProductManager(catalog)}><FiPackage /> Guest menu</button>
                      <button type="button" className="rm-topBtn rm-topBtn--violet" onClick={() => openAppearance(catalog)}><FiImage /> Appearance</button>
                      <button type="button" className="rm-topBtn" onClick={() => openEditCatalog(catalog)}><FiEdit2 /> Edit</button>
                      <button type="button" className={`rm-topBtn ${catalog.is_active ? "rm-topBtn--amber" : "rm-topBtn--green"}`} onClick={() => toggleCatalogStatus(catalog)}>
                        <FiPower /> {catalog.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!catalogs.length ? <tr><td colSpan="7" className="posMuted">No POS catalogs found.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </div>

      {catalogModalMode ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal" role="dialog" aria-modal="true" aria-label="POS catalog setup">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">{catalogModalMode === "edit" ? "Edit catalog" : "Add catalog"}</h3>
                <div className="rm-rates-subtitle">Define the outlet menu that can be assigned to devices.</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeCatalogModal} aria-label="Close">x</button>
            </div>
            {error ? <div className="posStateBanner">{error}</div> : null}
            <div className="posProductModalBody">
              <div className="posModalFormGrid">
                <label className="posModalField">
                  <span>Name</span>
                  <input className="posTextInput" value={catalogForm.name} onChange={(event) => setCatalogForm((current) => ({ ...current, name: event.target.value }))} placeholder="Rooftop guest ordering" />
                </label>
                <label className="posModalField">
                  <span>Outlet</span>
                  <select className="posSelect" value={catalogForm.outlet_id} onChange={(event) => setCatalogForm((current) => ({ ...current, outlet_id: event.target.value }))}>
                    <option value="">All outlets</option>
                    {outlets.map((outlet) => <option key={outlet.id} value={outlet.id}>{outlet.name}</option>)}
                  </select>
                </label>
                <label className="posModalField posModalField--wide">
                  <span>Description</span>
                  <input className="posTextInput" value={catalogForm.description} onChange={(event) => setCatalogForm((current) => ({ ...current, description: event.target.value }))} placeholder="Menu shown on rooftop guest tablets" />
                </label>
                <label className="posCatalogToggle">
                  <input type="checkbox" checked={catalogForm.is_active} onChange={(event) => setCatalogForm((current) => ({ ...current, is_active: event.target.checked }))} />
                  <span>Active catalog</span>
                </label>
              </div>
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeCatalogModal} disabled={savingCatalog}>Cancel</button>
              <button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveCatalog} disabled={savingCatalog}>{savingCatalog ? "Saving" : "Save catalog"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {appearanceCatalog ? (
        <div className="posModalOverlay" role="presentation">
          <div className="posTicketModal posProductModal posAppearanceModal" role="dialog" aria-modal="true" aria-label="Guest menu appearance">
            <div className="posTicketModalHeader">
              <div>
                <h3 className="rm-rates-title">Guest menu appearance</h3>
                <div className="rm-rates-subtitle">{appearanceCatalog.name}</div>
              </div>
              <button type="button" className="res-filterCloseBtn" onClick={closeAppearance} aria-label="Close">x</button>
            </div>
            {error ? <div className="posStateBanner">{error}</div> : null}
            <div className="posProductModalBody posAppearanceBody">
              <div className="posAppearanceSection">
                <span className="posAppearanceLabel">Theme</span>
                <div className="posThemeOptions" role="radiogroup" aria-label="Guest menu theme">
                  {GUEST_THEME_OPTIONS.map((theme) => {
                    const selected = appearanceTheme === theme.value;
                    return (
                      <button
                        key={theme.value}
                        type="button"
                        className={`posThemeOption ${selected ? "posThemeOption--selected" : ""}`}
                        onClick={() => setAppearanceTheme(theme.value)}
                        role="radio"
                        aria-checked={selected}
                      >
                        <span className="posThemeSwatch" style={{ background: theme.background }}>
                          <span style={{ background: theme.surface }} />
                          <i style={{ background: theme.accent }} />
                        </span>
                        <strong>{theme.label}</strong>
                        {selected ? <FiCheck aria-hidden="true" /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="posAppearanceSection">
                <span className="posAppearanceLabel">Guest languages</span>
                <div className="posAppearanceLanguageGrid">
                  <div className="posAppearanceLanguageOptions" role="group" aria-label="Enabled guest languages">
                    {GUEST_LANGUAGE_OPTIONS.map((language) => {
                      const enabled = appearanceEnabledLanguages.includes(language.value);
                      return <button key={language.value} type="button" className={enabled ? "posAppearanceLanguage--enabled" : ""} onClick={() => toggleAppearanceLanguage(language.value)} aria-pressed={enabled}>{enabled ? <FiCheck /> : null}{language.label}</button>;
                    })}
                  </div>
                  <label className="posModalField">
                    <span>Default language</span>
                    <select className="posSelect" value={appearanceDefaultLanguage} onChange={(event) => setAppearanceDefaultLanguage(event.target.value)}>
                      {GUEST_LANGUAGE_OPTIONS.filter((language) => appearanceEnabledLanguages.includes(language.value)).map((language) => <option key={language.value} value={language.value}>{language.label}</option>)}
                    </select>
                  </label>
                </div>
              </div>

              <div className="posAppearanceSection">
                <span className="posAppearanceLabel">Logo</span>
                <div className="posAppearanceLogoRow">
                  <div className="posAppearanceLogoPreview">
                    {appearanceLogoPreview ? <img src={appearanceLogoPreview} alt="Logo preview" /> : <FiImage aria-hidden="true" />}
                  </div>
                  <div className="posAppearanceLogoActions">
                    <label className="rm-topBtn rm-topBtn--cyan posFileButton">
                      <FiUploadCloud /> Upload logo
                      <input
                        key={appearanceLogoFile ? `${appearanceLogoFile.name}-${appearanceLogoFile.lastModified}` : "empty-logo"}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={selectAppearanceLogo}
                      />
                    </label>
                    {appearanceLogoPreview ? (
                      <button type="button" className="rm-topBtn rm-topBtn--amber" onClick={removeAppearanceLogo} disabled={savingAppearance}>
                        <FiTrash2 /> Remove
                      </button>
                    ) : null}
                    <span className="posAppearanceFileName">{appearanceLogoFile?.name || "JPG, PNG or WEBP · 5 MB max"}</span>
                  </div>
                </div>
              </div>

              <div className="posAppearanceSection">
                <span className="posAppearanceLabel">Background image</span>
                <div className="posAppearanceBackgroundPreview">
                  {appearanceBackgroundPreview ? (
                    <img src={appearanceBackgroundPreview} alt="Background preview" />
                  ) : (
                    <div><FiImage aria-hidden="true" /><span>No background image</span></div>
                  )}
                </div>
                <div className="posAppearanceBackgroundActions">
                  <label className="rm-topBtn rm-topBtn--cyan posFileButton">
                    <FiUploadCloud /> Upload background
                    <input
                      key={appearanceBackgroundFile ? `${appearanceBackgroundFile.name}-${appearanceBackgroundFile.lastModified}` : "empty-background"}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={selectAppearanceBackground}
                    />
                  </label>
                  {appearanceBackgroundPreview ? (
                    <button type="button" className="rm-topBtn rm-topBtn--amber" onClick={removeAppearanceBackground} disabled={savingAppearance}>
                      <FiTrash2 /> Remove
                    </button>
                  ) : null}
                  <span className="posAppearanceFileName">{appearanceBackgroundFile?.name || "JPG, PNG or WEBP · 10 MB max · landscape recommended"}</span>
                </div>
              </div>
            </div>
            <div className="posProductModalFooter">
              <button type="button" className="rm-topBtn" onClick={closeAppearance} disabled={savingAppearance}>Cancel</button>
              <button type="button" className="rm-topBtn rm-topBtn--green" onClick={saveAppearance} disabled={savingAppearance}>
                {savingAppearance ? "Saving" : "Save appearance"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {manageCatalog ? (
        <CatalogMenuManagerModal
          catalog={manageCatalog}
          businessUnitId={businessUnitId}
          onClose={closeProductManager}
          onChanged={loadCatalogs}
        />
      ) : null}
    </div>
  );
}
